/**
 * Kiosk ordering is opened only by the kiosk QR: the website link plus
 * `?kiosk=<token>`. The slug and custom-domain redirects drop the query
 * string, so the token is captured once at start-up and kept for this tab.
 * Opening the plain website link (a fresh /<slug> or custom-domain visit)
 * clears it, so the website QR always opens normal online ordering.
 */
const KIOSK_TOKEN_KEY = 'ordrciti.kioskToken';

export function captureKioskEntry(location: Pick<Location, 'search' | 'pathname'> = window.location): void {
  try {
    const token = (new URLSearchParams(location.search).get('kiosk') || '').trim();
    if (token) sessionStorage.setItem(KIOSK_TOKEN_KEY, token);
    // A reload on the restaurant page keeps the kiosk session.
    else if (!/^\/restaurant(-mobile)?\//.test(location.pathname)) sessionStorage.removeItem(KIOSK_TOKEN_KEY);
  } catch {
    // Storage blocked: the page simply runs as the normal website.
  }
}

export function kioskToken(): string {
  try {
    return sessionStorage.getItem(KIOSK_TOKEN_KEY) || '';
  } catch {
    return '';
  }
}

/** Query string for the public restaurant request ('' outside a kiosk session). */
export function kioskQuery(): string {
  const token = kioskToken();
  return token ? `?kiosk=${encodeURIComponent(token)}` : '';
}

/** A kiosk customer ordering without signing in (mobile verification off). */
export interface KioskGuest {
  phone: string;
  name: string;
}

/**
 * An Indian mobile as the website stores it (`+91` and 10 digits), or null.
 * Mirrors normalizeIndianMobile in the backend's kiosk feature.
 */
export function normalizeIndianMobile(value: string | null | undefined): string | null {
  let digits = String(value || '').replace(/[\s\-()]/g, '');
  if (digits.startsWith('+91')) digits = digits.slice(3);
  else if (digits.length === 12 && digits.startsWith('91')) digits = digits.slice(2);
  else if (digits.length === 11 && digits.startsWith('0')) digits = digits.slice(1);
  return /^[6-9]\d{9}$/.test(digits) ? `+91${digits}` : null;
}
